import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  CheckCircleIcon, 
  ExclamationCircleIcon, 
  KeyIcon,
  XCircleIcon
} from '@heroicons/react/24/solid';
import { ArrowRightOnRectangleIcon as LogoutIcon } from '@heroicons/react/24/outline';

const UserSettings = ({ currentUser, onLogout, onUpdateToken, isLoading }) => {
  const { accountStatus } = useAuth();
  const [derivToken, setDerivToken] = useState('');
  const [isTokenSaved, setIsTokenSaved] = useState(false);
  const [error, setError] = useState('');
  const [showTokenInput, setShowTokenInput] = useState(false);

  const handleSaveToken = (e) => {
    e.preventDefault();
    setError('');
    
    if (!derivToken.trim()) {
      setError('Please enter a valid Deriv API token');
      return;
    }
    
    try {
      onUpdateToken(derivToken);
      setIsTokenSaved(true);
      setShowTokenInput(false);
      setTimeout(() => setIsTokenSaved(false), 3000);
    } catch (err) {
      setError('Failed to save Deriv token');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Account Settings</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Manage your account and preferences</p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            <h3 className="ml-3 text-sm font-medium text-red-800">{error}</h3>
          </div>
        </div>
      )}

      {isTokenSaved && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="flex">
            <CheckCircleIcon className="h-5 w-5 text-green-400" />
            <h3 className="ml-3 text-sm font-medium text-green-800">Deriv API token saved!</h3>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg transition-colors duration-200">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Profile Information</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Your account details</p>
        </div>
        <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200 dark:divide-gray-700">
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 sm:mt-0 sm:col-span-2">
                {currentUser?.email || 'N/A'}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Account Type</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 sm:mt-0 sm:col-span-2">
                {currentUser?.role === 'super_admin' ? 'Administrator' : 'Standard User'}
              </dd>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Account Status</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                <div className="flex items-center">
                  {accountStatus?.active ? (
                    <>
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span className="text-green-700 dark:text-green-400">Active</span>
                    </>
                  ) : (
                    <>
                      <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                      <span className="text-red-700 dark:text-red-400">Inactive</span>
                      {accountStatus?.message && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                          {accountStatus.message}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg transition-colors duration-200">
        <div className="px-4 py-5 sm:px-6">
          {/*<h3 className="text-lg font-medium text-gray-900 dark:text-white">API Settings</h3>*/}
          {/*<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Manage your Deriv API integration</p>*/}
          
          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md transition-colors duration-200">
            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">Need help with your API setup?</h4>
            <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
              <li className="flex items-start">
                <span className="mr-2">📞</span>
                <a href="tel:+254717325657" className="hover:underline">+254 717 325 657</a>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✉️</span>
                <a href="mailto:murungaevans84@gmail.com" className="hover:underline">murungaevans84@gmail.com</a>
              </li>
            </ul>
            {/*<div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded border border-blue-100 dark:border-blue-900 transition-colors duration-200">*/}
            {/*  <p className="text-xs text-gray-600 dark:text-gray-300">*/}
            {/*    <span className="font-medium">Important:</span> Never share your API token with anyone. */}
            {/*    For security reasons, always verify the recipient's identity before sharing any sensitive information.*/}
            {/*  </p>*/}
            {/*</div>*/}
          </div>
        </div>
        <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-5 sm:p-0">
          {/*<dl className="sm:divide-y sm:divide-gray-200 dark:divide-gray-700">*/}
          {/*  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">*/}
          {/*    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Deriv API Token</dt>*/}
          {/*    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 sm:mt-0 sm:col-span-2">*/}
          {/*      {showTokenInput ? (*/}
          {/*        <form onSubmit={handleSaveToken} className="flex space-x-2">*/}
          {/*          <input*/}
          {/*            type="password"*/}
          {/*            value={derivToken}*/}
          {/*            onChange={(e) => setDerivToken(e.target.value)}*/}
          {/*            className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"*/}
          {/*            placeholder="Enter your Deriv API token"*/}
          {/*            autoComplete="off"*/}
          {/*          />*/}
          {/*          <button*/}
          {/*            type="submit"*/}
          {/*            disabled={isLoading}*/}
          {/*            className="px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"*/}
          {/*          >*/}
          {/*            Save*/}
          {/*          </button>*/}
          {/*          <button*/}
          {/*            type="button"*/}
          {/*            onClick={() => setShowTokenInput(false)}*/}
          {/*            className="px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"*/}
          {/*          >*/}
          {/*            Cancel*/}
          {/*          </button>*/}
          {/*        </form>*/}
          {/*      ) : (*/}
          {/*        <div className="flex items-center">*/}
          {/*          <KeyIcon className="h-5 w-5 text-gray-400 mr-2" />*/}
          {/*          <span className="mr-4">••••••••••••••••</span>*/}
          {/*          <button*/}
          {/*            type="button"*/}
          {/*            onClick={() => setShowTokenInput(true)}*/}
          {/*            className="text-sm font-medium text-blue-600 hover:text-blue-500"*/}
          {/*          >*/}
          {/*            Update*/}
          {/*          </button>*/}
          {/*        </div>*/}
          {/*      )}*/}
          {/*    </dd>*/}
          {/*  </div>*/}
          {/*</dl>*/}
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg font-medium text-gray-900">Danger Zone</h3>
          <p className="mt-1 text-sm text-gray-500">Actions that cannot be undone</p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Sign out</h4>
              <p className="text-sm text-gray-500">Sign out of your account</p>
            </div>
            <button
              type="button"
              onClick={onLogout}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <LogoutIcon className="-ml-1 mr-2 h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSettings;
